import { appAxios } from '@/axios/appAxios';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    CustomJsonApiPaths,
    CustomJsonApiRequest,
    CustomJsonDraft,
    CustomJsonDraftCreate,
    CustomJsonDraftIdentity,
    CustomJsonDraftQuery,
    CustomJsonDraftUpdate,
    CustomJsonValue,
} from '@/shared/interfaces/CustomJsonInterfaces';

const CUSTOM_JSON_HEADERS = {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
};

const requestConfig: AxiosRequestConfig = { headers: CUSTOM_JSON_HEADERS };
const encodePathPart = (value: string | number): string => encodeURIComponent(String(value));

/** Shared path builders for the custom JSON draft API. */
export const CUSTOM_JSON_API: CustomJsonApiPaths = {
    all: '/drafts',
    byId: (id) => `/drafts/${encodePathPart(id)}`,
    byResource: (resourceType, resourceId) => `/drafts/${encodePathPart(resourceType)}/${encodePathPart(resourceId)}`,
    byResourceKey: ({ draftType, resourceType, resourceId, draftKey }) =>
        `/drafts/${encodePathPart(draftType)}/${encodePathPart(resourceType)}/${encodePathPart(
            resourceId,
        )}/${encodePathPart(draftKey)}`,
};

function buildRequest<TAttributes>(attributes: TAttributes, id?: string): CustomJsonApiRequest<TAttributes> {
    return {
        data: {
            type: 'drafts',
            ...(id ? { id } : {}),
            attributes,
        },
    };
}

function buildDraftListPath(query: CustomJsonDraftQuery = {}): string {
    const queryParts: string[] = [];

    Object.keys(query.filters || {}).forEach((key) => {
        const value = query.filters![key as keyof typeof query.filters];

        if (value !== undefined) {
            queryParts.push(`filter[${key}]=${encodeURIComponent(String(value))}`);
        }
    });

    if (query.sort) {
        queryParts.push(`sort=${encodeURIComponent(query.sort)}`);
    }
    if (query.page?.number) {
        queryParts.push(`page[number]=${query.page.number}`);
    }
    if (query.page?.size) {
        queryParts.push(`page[size]=${query.page.size}`);
    }

    return queryParts.length ? `${CUSTOM_JSON_API.all}?${queryParts.join('&')}` : CUSTOM_JSON_API.all;
}

/* === Draft Collection: /drafts === */

export function createDraft<TPayload = CustomJsonValue>(
    draft: CustomJsonDraftCreate<TPayload>,
): Promise<AxiosResponse<CustomJsonDraft<TPayload>>> {
    return appAxios.post(CUSTOM_JSON_API.all, buildRequest(draft), requestConfig);
}

export function getAllDrafts<TPayload = CustomJsonValue>(
    query: CustomJsonDraftQuery = {},
): Promise<AxiosResponse<Array<CustomJsonDraft<TPayload>>>> {
    return appAxios.get(buildDraftListPath(query), requestConfig);
}

/* === Draft ID: /drafts/{id} === */

export function getDraftByID<TPayload = CustomJsonValue>(
    id: string,
): Promise<AxiosResponse<CustomJsonDraft<TPayload>>> {
    return appAxios.get(CUSTOM_JSON_API.byId(id), requestConfig);
}

export function updateDraftById<TPayload = CustomJsonValue>(
    id: string,
    draft: CustomJsonDraftUpdate<TPayload>,
): Promise<AxiosResponse<CustomJsonDraft<TPayload>>> {
    return appAxios.patch(CUSTOM_JSON_API.byId(id), buildRequest(draft, id), requestConfig);
}

export function deleteDraftByID(id: string): Promise<AxiosResponse<void>> {
    return appAxios.delete(CUSTOM_JSON_API.byId(id), requestConfig);
}

/* === Drafts by Resource: /drafts/{resourceType}/{resourceId} === */

export function getDraftsByResource<TPayload = CustomJsonValue>(
    resourceType: string,
    resourceId: number,
): Promise<AxiosResponse<Array<CustomJsonDraft<TPayload>>>> {
    return appAxios.get(CUSTOM_JSON_API.byResource(resourceType, resourceId), requestConfig);
}

/* === Single Draft by Resource Key: /drafts/{draftType}/{resourceType}/{resourceId}/{draftKey} === */

export function getDraftByFullResource<TPayload = CustomJsonValue>(
    identity: CustomJsonDraftIdentity,
): Promise<AxiosResponse<CustomJsonDraft<TPayload>>> {
    return appAxios.get(CUSTOM_JSON_API.byResourceKey(identity), requestConfig);
}

export function updateDraftByFullResource<TPayload = CustomJsonValue>(
    identity: CustomJsonDraftIdentity,
    payload: TPayload,
): Promise<AxiosResponse<CustomJsonDraft<TPayload>>> {
    return appAxios.put(CUSTOM_JSON_API.byResourceKey(identity), buildRequest({ payload }), requestConfig);
}

export function createDraftByFullResource<TPayload = CustomJsonValue>(
    identity: CustomJsonDraftIdentity,
    payload: TPayload,
): Promise<AxiosResponse<CustomJsonDraft<TPayload>>> {
    return updateDraftByFullResource(identity, payload);
}
